/**
 * Closing tasks from commits (commit ↔ task links, slice 3). Pure.
 *
 * "Fixes WEB-4" closes WEB-4 when the commit has LANDED: it is on the remote
 * default branch (`pushed`, src-tauri code_repo_log.rs), as GitHub and Linear
 * close on merge to the default branch. Three guards keep it predictable:
 *
 * - the board must have "Close tasks from commits" on (`closeFromCommits`),
 *   and only commits made after it was switched on count
 *   (`closeFromCommitsSince`), so turning it on never closes old tasks;
 * - each (task, commit) closes at most ONCE: the close is recorded on the task
 *   as a `commitLinks` entry `{ sha, closed: true, auto: true }`, so a task a
 *   person reopens stays open — only a NEW commit can close it again;
 * - a task already done or dismissed is left alone.
 *
 * Closing = status Done (`done: true`, `doneAt`) and, when the board has a
 * list named like Done, moved to the end of it.
 */
import { closingKeysOfCommit } from '../../../services/git/closingKeywords';
import { statusPatch, withDoneAt } from './taskStatus';
import { moveTaskToList } from './moveTaskToList';
import { commitLinksOf } from './taskCommitLinks';

const DONE_LIST = /^(done|completed?|closed|finished|shipped|released|erledigt|fertig|abgeschlossen|geschlossen)$/i;

/** The board's done list: the first list named Done / Completed / Erledigt …, or null. */
export function doneListOf(board) {
  const lists = [...(board?.groups || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return lists.find((g) => DONE_LIST.test(String(g.name || '').trim())) || null;
}

const sinceMs = (board) => {
  const t = Date.parse(board?.closeFromCommitsSince || '');
  return Number.isFinite(t) ? t : null;
};

/** Has this task already been closed by this commit once? */
const closedBefore = (task, sha) => commitLinksOf(task).some((l) => l.closed && String(l.sha) === String(sha));

/**
 * Which tasks of `board` the `commits` close: [{ taskId, key, commit }].
 * `keysOf(task)` gives every key the task answers to (current + old prefixes).
 */
export function planAutoClose(board, commits, keysOf) {
  if (!board?.closeFromCommits) return [];
  const since = sinceMs(board);
  if (since == null) return [];
  const byKey = new Map();
  for (const task of board.tasks || []) {
    for (const key of keysOf(task) || []) if (key) byKey.set(String(key).toUpperCase(), task);
  }
  const plan = [];
  const taken = new Set();
  // Oldest first: the first commit that closes a task is the one recorded.
  const ordered = [...(commits || [])].sort((a, b) => (a.time || 0) - (b.time || 0));
  for (const commit of ordered) {
    if (!commit?.pushed || !((commit.time || 0) * 1000 >= since)) continue;
    for (const key of closingKeysOfCommit(commit)) {
      const task = byKey.get(key);
      if (!task || task.done || taken.has(task.id) || closedBefore(task, commit.oid)) continue;
      taken.add(task.id);
      plan.push({ taskId: task.id, key, commit });
    }
  }
  return plan;
}

/** `board` with the plan applied (returns `board` itself for an empty plan). */
export function applyAutoClose(board, plan, { now = new Date().toISOString() } = {}) {
  if (!plan?.length) return board;
  const done = doneListOf(board);
  let tasks = board.tasks;
  for (const { taskId, commit } of plan) {
    tasks = tasks.map((t) => {
      if (t.id !== taskId) return t;
      const marker = {
        sha: commit.oid,
        repoId: commit.repo?.id != null ? String(commit.repo.id) : commit.repoId != null ? String(commit.repoId) : null,
        summary: String(commit.summary || '').slice(0, 200),
        author: commit.author || '',
        time: Number.isFinite(commit.time) ? commit.time : null,
        branch: commit.branch || '',
        closed: true,
        auto: true,
        linkedAt: now,
      };
      return { ...t, ...withDoneAt(t, statusPatch('done'), now), commitLinks: [...commitLinksOf(t), marker], updatedAt: now };
    });
    if (done) {
      const task = tasks.find((t) => t.id === taskId);
      if (task && task.groupId !== done.id) tasks = moveTaskToList(tasks, taskId, done.id, now);
    }
  }
  return { ...board, tasks };
}
